import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, Clock, FileText, MagnifyingGlass,
  NotePencil, Palette, Presentation, Sparkle, Table,
} from '@phosphor-icons/react';
import { AppSquircle } from '../../AppSquircle';
import type { BaseEntityType } from '../../../types/entities';
import { useEntityIcon } from '../../../hooks/useEntityIcon';
import useEntityStore from '../../../stores/entityStore';
import { useSpaceEntities } from '../../../stores/spaceEntityStore';
import useSpaceStore from '../../../stores/spaceStore';
import useDataspaceTreeStore from '../../../stores/dataspaceTreeStore';
import { useCommandRegistryStore } from '../../../stores/commandRegistryStore';
import { entityService } from '../../../services/entityService';
import type { EntitySearchResult } from '../../../services/entityService';
import { searchUniversal } from '../../../services/searchService';
import { SPACE_SCOPED_ENTITIES } from '../../../utils/featureFlags';
import { createFileLogger } from '../../../utils/logger';
import './GlobalEntitySearch.scss';

const log = createFileLogger('GLOBAL_ENTITY_SEARCH');

/**
 * Fuzzy entity name scoring for client-side filtering.
 * Returns 0 (no match) or a positive score. Higher = better match.
 *
 * Tiers (highest to lowest):
 *   100 — exact substring (e.g. "doc" ↔ "My Document")
 *    80 — all query words are substrings (multi-word)
 *    70 — acronym: query matches word initials (e.g. "mr" ↔ "Monthly Report")
 *    50 — character subsequence (e.g. "mthly" ↔ "Monthly")
 */
function fuzzyScore(name: string, query: string): number {
  const n = name.toLowerCase();
  const q = query.toLowerCase();
  if (!q) return 0;

  if (n.includes(q)) return 100;

  const qWords = q.split(/\s+/).filter(Boolean);
  if (qWords.length > 1 && qWords.every(w => n.includes(w))) return 80;

  const initials = n.split(/[\s\-_.]+/).filter(Boolean).map(w => w[0]).join('');
  if (initials.length >= q.length && (initials.startsWith(q) || initials.includes(q))) return 70;

  let qi = 0;
  for (let ni = 0; ni < n.length && qi < q.length; ni++) {
    if (n[ni] === q[qi]) qi++;
  }
  if (qi === q.length) return Math.max(30, 55 - q.length * 2);

  return 0;
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Doc: 'Document', Note: 'Note', Page: 'Page', File: 'File',
  Contact: 'Contact', Task: 'Task', Goal: 'Goal', Project: 'Project',
  Agent: 'Agent', Thread: 'Thread', Space: 'Space', Group: 'Folder',
};

function typeLabel(et: string): string {
  if (ENTITY_TYPE_LABELS[et]) return ENTITY_TYPE_LABELS[et];
  if (et.startsWith('File.')) return 'File';
  if (et.startsWith('Integration.')) return et.replace('Integration.', '');
  return et;
}

// ── Quickbar item definitions ─────────────────────────────────────────────────

interface QuickbarItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const CREATE_ITEMS: QuickbarItem[] = [
  { id: 'new-doc',    label: 'Doc',    icon: <FileText    size={16} weight="duotone" style={{ color: '#2563eb' }} /> },
  { id: 'new-note',   label: 'Note',   icon: <NotePencil  size={16} weight="duotone" style={{ color: '#f59e0b' }} /> },
  { id: 'new-canvas', label: 'Canvas', icon: <Palette     size={16} weight="duotone" style={{ color: '#9333ea' }} /> },
  { id: 'new-sheet',  label: 'Sheet',  icon: <Table       size={16} weight="duotone" style={{ color: '#10b981' }} /> },
  { id: 'new-slides', label: 'Slides', icon: <Presentation size={16} weight="duotone" style={{ color: '#f97316' }} /> },
];

const APP_ITEMS: QuickbarItem[] = [
  { id: 'app-drive',       label: 'Drive',       icon: <AppSquircle appId="entityBrowser2" size="sm" /> },
  { id: 'app-automations', label: 'Automations', icon: <AppSquircle appId="automations"    size="sm" /> },
  { id: 'app-contacts',    label: 'Contacts',    icon: <AppSquircle appId="contacts"       size="sm" /> },
  { id: 'app-mail',        label: 'Mail',        icon: <AppSquircle appId="mail"           size="sm" /> },
];

const QUICKBAR_ITEMS: QuickbarItem[] = [...CREATE_ITEMS, ...APP_ITEMS];

// ── Drive view items — searchable navigation targets within RiverDriveApp ─────

interface DriveViewItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  view: { mode: string; [key: string]: any };
}

const DRIVE_SQUIRCLE = <AppSquircle appId="entityBrowser2" size="sm" />;

const DRIVE_VIEW_ITEMS: DriveViewItem[] = [
  { id: 'drive-files',        label: 'My Drive',          icon: DRIVE_SQUIRCLE, view: { mode: 'files' } },
  { id: 'drive-recents',      label: 'Recents',            icon: DRIVE_SQUIRCLE, view: { mode: 'recents' } },
  { id: 'drive-shared',       label: 'Shared with Me',     icon: DRIVE_SQUIRCLE, view: { mode: 'shared' } },
  { id: 'drive-web',          label: 'Browsing History',   icon: DRIVE_SQUIRCLE, view: { mode: 'web' } },
  { id: 'drive-contacts',     label: 'Contacts',           icon: DRIVE_SQUIRCLE, view: { mode: 'people' } },
  { id: 'drive-work',         label: 'Tasks & Goals',      icon: DRIVE_SQUIRCLE, view: { mode: 'work' } },
  { id: 'drive-agents',       label: 'Automations',        icon: DRIVE_SQUIRCLE, view: { mode: 'automations' } },
  { id: 'drive-skills',       label: 'Skills',             icon: DRIVE_SQUIRCLE, view: { mode: 'skills' } },
  { id: 'drive-knowledge',    label: 'Knowledge',          icon: DRIVE_SQUIRCLE, view: { mode: 'knowledge' } },
  { id: 'drive-memories',     label: 'Memories',           icon: DRIVE_SQUIRCLE, view: { mode: 'memories' } },
  { id: 'drive-integrations', label: 'Integrations',       icon: DRIVE_SQUIRCLE, view: { mode: 'integrations' } },
  { id: 'drive-activity',     label: 'Activity',           icon: DRIVE_SQUIRCLE, view: { mode: 'activity' } },
  { id: 'drive-import',       label: 'Import & Sources',   icon: DRIVE_SQUIRCLE, view: { mode: 'import-sources' } },
  { id: 'drive-tools-dev',    label: 'Tool Registry',      icon: DRIVE_SQUIRCLE, view: { mode: 'tools-dev' } },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface GlobalEntitySearchProps {
  onClose: () => void;
}

type ActiveZone = 'quickbar' | 'results' | null;

const GlobalEntitySearch: React.FC<GlobalEntitySearchProps> = ({ onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [activeZone, setActiveZone] = useState<ActiveZone>(null);
  const [quickbarIndex, setQuickbarIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [backendResults, setBackendResults] = useState<EntitySearchResult[]>([]);
  const [backendLoading, setBackendLoading] = useState(false);
  const [isShiftHeld, setIsShiftHeld] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const quickbarRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const navigate = useNavigate();
  const { executeCommand } = useCommandRegistryStore();
  const activeSpace = useSpaceStore(s => s.activeSpace);
  const currentSpaceId = activeSpace?.id;
  const hasActiveSpace = !!activeSpace;

  // Source the client-side entity pool from the right cache:
  //   - Flag ON  → spaceEntityStore (only entities reachable from the current space)
  //   - Flag OFF → legacy useEntityStore.entities (dataspace-wide bulk)
  // On Home with the flag on there is no active space; the array is empty and
  // the modal relies on backend search via /api/search/universal.
  const legacyEntities = useEntityStore(s => s.entities);
  const spaceEntities = useSpaceEntities(SPACE_SCOPED_ENTITIES ? currentSpaceId : null);
  const allEntities = SPACE_SCOPED_ENTITIES ? spaceEntities : legacyEntities;
  const { getEntityIcon } = useEntityIcon({ size: 16 });

  // Creates a minimal space and navigates to it with an optional URL suffix.
  // Used when the modal is triggered from outside a space (e.g. Home page).
  const createSpaceAndNavigate = useCallback(async (urlSuffix: string, spaceName: string) => {
    try {
      const { default: useSystemStore } = await import('../../../stores/systemStore');
      const newSpace = await useSystemStore.getState().actions.createSpace({
        name: spaceName,
        skipDefaultWindow: true,
        skipProjectLimit: true,
        settings: {
          isChatVisible: false,
          viewMode: 'focused',
          sidebar: { isVisible: true, width: 300, mode: 'normal' },
        },
      });
      if (newSpace?.id) {
        navigate(`/spaces/${newSpace.id}${urlSuffix}`);
      }
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to create space:', err);
    }
  }, [navigate]);
  const getNode = useDataspaceTreeStore(s => s.getNode);

  // Build id → name map for parent path lookup
  const entityNameById = useMemo(() => {
    const map = new Map<string, string>();
    allEntities.forEach(e => { if (e._id) map.set(e._id, e.name || ''); });
    return map;
  }, [allEntities]);

  const getParentName = useCallback((entityId: string): string | null => {
    const node = getNode(entityId);
    if (!node?.parentId) return null;
    return entityNameById.get(node.parentId) || null;
  }, [getNode, entityNameById]);

  // Walk the dataspace tree upward to find the Space entity that owns this entity.
  const findEntitySpaceId = useCallback((entityId: string): string | null => {
    const visited = new Set<string>();
    let nodeId = entityId;
    while (nodeId) {
      if (visited.has(nodeId)) break;
      visited.add(nodeId);
      const node = getNode(nodeId);
      if (!node?.parentId) break;
      const parentEntity = allEntities.find(e => e._id === node.parentId);
      if (parentEntity?.entityType === 'Space') return node.parentId;
      nodeId = node.parentId;
    }
    return null;
  }, [getNode, allEntities]);

  const getSpaceName = useCallback((entityId: string): string | null => {
    const spaceId = findEntitySpaceId(entityId);
    if (!spaceId) return null;
    return entityNameById.get(spaceId) || null;
  }, [findEntitySpaceId, entityNameById]);

  // Client-side results — fuzzy-scored for better typo/initials tolerance
  const clientResults = useMemo((): BaseEntityType[] => {
    const inCurrentSpace = (id: string) => !!currentSpaceId && findEntitySpaceId(id) === currentSpaceId;

    if (!inputValue.trim()) {
      return [...allEntities]
        .filter(e => !e.trashed && !(e as any).hidden)
        .sort((a, b) => {
          const aSpace = inCurrentSpace(a._id) ? 1 : 0;
          const bSpace = inCurrentSpace(b._id) ? 1 : 0;
          if (bSpace !== aSpace) return bSpace - aSpace;
          const da = new Date(a.updated_at || a.created_at || 0).getTime();
          const db = new Date(b.updated_at || b.created_at || 0).getTime();
          return db - da;
        })
        .slice(0, 10);
    }
    const q = inputValue.trim();
    return allEntities
      .filter(e => !e.trashed && !(e as any).hidden)
      .map(e => ({ entity: e, baseScore: Math.max(fuzzyScore(e.name || '', q), fuzzyScore(e.entityType || '', q)) }))
      .filter(({ baseScore }) => baseScore > 0)
      .map(({ entity, baseScore }) => ({ entity, score: baseScore + (inCurrentSpace(entity._id) ? 20 : 0) }))
      .sort((a, b) => b.score - a.score)
      .map(({ entity }) => entity)
      .slice(0, 12);
  }, [allEntities, inputValue, currentSpaceId, findEntitySpaceId]);

  // Debounced backend search — uses universal search (navigational mode) so that
  // content inside Docs, PDFs, and Notes is searched via content_chunks, not just
  // entity-level fields. Falls back to entity-only search if universal fails.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue.trim() || inputValue.trim().length < 2) {
      abortRef.current?.abort();
      setBackendResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setBackendLoading(true);
      try {
        const response = await searchUniversal({
          query: inputValue.trim(),
          mode: 'navigational',
          includePassages: false,
          limit: 20,
        });
        if (abortRef.current !== ac) return;
        // Map UniversalSearchResult to EntitySearchResult shape for existing rendering code
        const mapped: EntitySearchResult[] = (response.results ?? []).map(r => ({
          entity_id: r.entity_id,
          name: r.name,
          entityType: r.entity_type,
          icon: r.icon,
          score: r.score,
          snippet: r.snippet ?? undefined,
        }));
        setBackendResults(mapped);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        // Fall back to entity-only search on unexpected errors
        try {
          const results = await entityService.searchEntities(inputValue.trim(), undefined, 20, false);
          if (abortRef.current === ac) setBackendResults(results);
        } catch {
          setBackendResults([]);
        }
      } finally {
        if (abortRef.current === ac) setBackendLoading(false);
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [inputValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Track shift key so the selected-item label and footer hint update live
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(true); };
    const onUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setIsShiftHeld(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Dedup backend extras against client results
  const clientIds = useMemo(() => new Set(clientResults.map(e => e._id)), [clientResults]);
  const backendExtras = useMemo((): EntitySearchResult[] => {
    if (!inputValue.trim()) return [];
    return backendResults
      .filter(r => !clientIds.has(r.entity_id))
      .sort((a, b) => {
        const aInSpace = currentSpaceId && findEntitySpaceId(a.entity_id) === currentSpaceId ? 1 : 0;
        const bInSpace = currentSpaceId && findEntitySpaceId(b.entity_id) === currentSpaceId ? 1 : 0;
        return bInSpace - aInSpace;
      });
  }, [backendResults, clientIds, inputValue, currentSpaceId, findEntitySpaceId]);

  const totalCount = clientResults.length + backendExtras.length;

  // Drive views — fuzzy-matched against view labels and "drive <label>"
  const matchingDriveViews = useMemo((): DriveViewItem[] => {
    if (!inputValue.trim()) return [];
    const q = inputValue.trim();
    return DRIVE_VIEW_ITEMS
      .map(item => ({ item, score: Math.max(fuzzyScore(item.label, q), fuzzyScore(`drive ${item.label}`, q)) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item);
  }, [inputValue]);

  // Reset zone & selection on query change
  useEffect(() => {
    setActiveZone(null);
    setSelectedIndex(0);
  }, [inputValue]);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll selected result into view
  useEffect(() => {
    if (activeZone === 'results') {
      listRef.current?.querySelector('.global-entity-search-item.selected')
        ?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex, activeZone]);

  // Scroll focused quickbar item into view
  useEffect(() => {
    if (activeZone === 'quickbar') {
      quickbarRef.current?.querySelector('.quickbar-item.focused')
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [quickbarIndex, activeZone]);

  // ── Quickbar actions ──────────────────────────────────────────────────────

  const fireQuickbarItem = useCallback(async (item: QuickbarItem) => {
    onClose();
    const spaceId = currentSpaceId;

    // App items — when no active space, create one and navigate with ?openApp=
    const APP_ID_MAP: Record<string, string> = {
      'app-drive':       'entityBrowser2',
      'app-automations': 'automations',
      'app-contacts':    'contacts',
      'app-mail':        'mail',
    };
    const appId = APP_ID_MAP[item.id];
    if (appId) {
      if (!hasActiveSpace) {
        sessionStorage.setItem('pendingAppOpen', JSON.stringify({ appId, appName: item.label }));
        createSpaceAndNavigate(`?openApp=${appId}`, item.label);
      } else {
        const windowId = await useSpaceStore.getState().actions.createWindow({ appType: appId });
        useSpaceStore.getState().setActiveWindow(windowId);
      }
      return;
    }

    // Create items — when no active space, store intent and create space
    const CREATE_COMMAND_MAP: Record<string, string> = {
      'new-doc':    'doc',
      'new-note':   'note',
      'new-canvas': 'canvas',
      'new-sheet':  'sheet',
      'new-slides': 'slides',
    };
    const createType = CREATE_COMMAND_MAP[item.id];
    if (createType) {
      if (!hasActiveSpace) {
        sessionStorage.setItem('pendingEntityCreate', JSON.stringify({ type: createType }));
        createSpaceAndNavigate('', item.label);
      } else {
        let result: any;
        if (item.id === 'new-doc') {
          result = await executeCommand('docs_create', { name: 'Untitled Document', spaceId, createWindow: true });
        } else if (item.id === 'new-note') {
          result = await executeCommand('notes_create', { title: 'Untitled', spaceId, createWindow: true });
        } else if (item.id === 'new-canvas') {
          result = await executeCommand('canvas_create', { title: 'Untitled Canvas', spaceId, createWindow: true });
        } else if (item.id === 'new-sheet') {
          result = await executeCommand('sheets_create', { title: 'New Sheet', spaceId, createWindow: true });
        } else if (item.id === 'new-slides') {
          result = await executeCommand('presentations_create', { title: 'New Presentation', spaceId, createWindow: true });
        }
        const windowId = result?.data?.window?.id;
        if (windowId) {
          useSpaceStore.getState().setActiveWindow(windowId);
        }
      }
    }
  }, [executeCommand, onClose, currentSpaceId, hasActiveSpace, createSpaceAndNavigate]);

  // ── Entity selection ──────────────────────────────────────────────────────
  //
  // Two modes:
  //   handleJumpTo  — navigate to the entity in a dedicated space (Enter, click)
  //   handleOpenHere — open a window for the entity in the current space (Shift+Enter, Shift+click)
  //
  // When there is no active space both actions fall back to navigate.

  const handleOpenHere = useCallback((entity: BaseEntityType) => {
    log.log('[GlobalEntitySearch] Open here:', entity.name, entity.entityType);
    onClose();

    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${entity._id}`, entity.name || 'Space');
      return;
    }

    if (
      entity.entityType === 'Group' ||
      entity.entityType === 'Integration' ||
      entity.entityType?.startsWith('Integration.')
    ) {
      executeCommand('space_createWindow', {
        appType: 'entityBrowser2',
        title: entity.name || 'Folder',
        entity,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (parsed.success) log.log('[GlobalEntitySearch] EntityBrowser opened:', parsed);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_createWindow result:', err);
          }
        },
      });
    } else {
      executeCommand('space_open_window', {
        entityId: entity._id,
        callback: (result: string) => {
          try {
            const parsed = JSON.parse(result);
            if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open entity:', parsed.error);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_open_window result:', err);
          }
        },
      });
    }
  }, [executeCommand, onClose, hasActiveSpace, createSpaceAndNavigate]);

  const handleJumpTo = useCallback((entity: BaseEntityType) => {
    log.log('[GlobalEntitySearch] Jump to:', entity.name, entity.entityType);
    onClose();
    const spaceId = findEntitySpaceId(entity._id);
    if (!spaceId || spaceId === currentSpaceId) {
      // Already in the entity's space (or no home space found) — open it here
      handleOpenHere(entity);
    } else {
      navigate(`/spaces/${spaceId}?openEntity=${entity._id}`);
    }
  }, [onClose, findEntitySpaceId, currentSpaceId, navigate, handleOpenHere]);

  const handleOpenHereBackend = useCallback((result: EntitySearchResult) => {
    const full = allEntities.find(e => e._id === result.entity_id);
    if (full) { handleOpenHere(full); return; }

    onClose();
    if (!hasActiveSpace) {
      createSpaceAndNavigate(`?openEntity=${result.entity_id}`, result.name || 'Space');
      return;
    }
    executeCommand('space_open_window', {
      entityId: result.entity_id,
      callback: (res: string) => {
        try {
          const parsed = JSON.parse(res);
          if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open backend entity:', parsed.error);
        } catch (err) {
          log.error('[GlobalEntitySearch] Failed to parse result:', err);
        }
      },
    });
  }, [allEntities, handleOpenHere, onClose, executeCommand, hasActiveSpace, createSpaceAndNavigate]);

  const handleJumpToBackend = useCallback((result: EntitySearchResult) => {
    const full = allEntities.find(e => e._id === result.entity_id);
    if (full) { handleJumpTo(full); return; }
    onClose();
    const spaceId = findEntitySpaceId(result.entity_id);
    if (!spaceId || spaceId === currentSpaceId) {
      handleOpenHereBackend(result);
    } else {
      navigate(`/spaces/${spaceId}?openEntity=${result.entity_id}`);
    }
  }, [allEntities, handleJumpTo, onClose, findEntitySpaceId, currentSpaceId, navigate, handleOpenHereBackend]);

  const handleSelectDriveView = useCallback(async (item: DriveViewItem) => {
    onClose();
    if (!hasActiveSpace) {
      sessionStorage.setItem('pendingAppOpen', JSON.stringify({ appId: 'entityBrowser2', appName: item.label }));
      createSpaceAndNavigate(`?openApp=entityBrowser2`, item.label);
      return;
    }
    const newWindowId = await useSpaceStore.getState().actions.createWindow({
      appType: 'entityBrowser2',
      title: item.label,
      applicationState: { drive: { activeView: item.view } },
    });
    useSpaceStore.getState().setActiveWindow(newWindowId);
  }, [onClose, hasActiveSpace, createSpaceAndNavigate]);

  // Fires the prompt inside the current space via the spacechat event bus.
  // Falls back to creating a new space if there is no active space.
  const handleLaunchInCurrentSpace = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();
    if (hasActiveSpace) {
      window.dispatchEvent(new CustomEvent('spacechat:sendPrompt', {
        detail: { prompt: text, threadTitle: text.slice(0, 60) },
      }));
    } else {
      try {
        const { fireAgentInNewSpace } = await import('../../../services/agentFireService');
        const { spaceId, threadId, runId } = await fireAgentInNewSpace({ prompt: text });
        navigate(`/spaces/${spaceId}?thread=${threadId}`, {
          state: runId ? { activeRunId: runId } : undefined,
        });
      } catch (err) {
        log.error('[GlobalEntitySearch] Failed to launch prompt in new space:', err);
      }
    }
  }, [inputValue, onClose, hasActiveSpace, navigate]);

  // Always creates a new space for the prompt regardless of current context.
  const handleLaunchInNewSpace = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();
    try {
      const { fireAgentInNewSpace } = await import('../../../services/agentFireService');
      const { spaceId, threadId, runId } = await fireAgentInNewSpace({ prompt: text });
      navigate(`/spaces/${spaceId}?thread=${threadId}`, {
        state: runId ? { activeRunId: runId } : undefined,
      });
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to launch prompt in new space:', err);
    }
  }, [inputValue, onClose, navigate]);

  // Opens a SearchResults entity window with the current search results.
  const handleLaunchSearch = useCallback(async () => {
    const text = inputValue.trim();
    if (!text) return;
    onClose();

    const buildSearchEntity = async (spaceId?: string) => {
      const backendIds = new Set(backendResults.map(r => r.entity_id));
      const clientExtras = clientResults.filter(e => !backendIds.has(e._id));
      const mergedResults = [
        ...backendResults.map(r => ({
          id: r.entity_id, type: 'entity', entityType: r.entityType,
          name: r.name, icon: r.icon || '📄', description: r.snippet || r.entityType || '',
          metadata: { matchType: 'server', matchScore: r.score || 0, lastModified: undefined, path: '' },
        })),
        ...clientExtras.map(e => ({
          id: e._id, type: 'entity', entityType: e.entityType,
          name: e.name || 'Unnamed', icon: '📄', description: e.entityType || '',
          metadata: { matchType: 'client', matchScore: 0, lastModified: undefined, path: '' },
        })),
      ];
      const timestamp = new Date().toISOString();
      return useEntityStore.getState().actions.createEntity({
        entityType: 'SearchResults',
        name: `Search: ${text}`,
        ...(spaceId ? { spaceId } : {}),
        skeleton: {
          '@type': 'SearchResults',
          query: text,
          results: mergedResults,
          timestamp,
          resultCount: mergedResults.length,
          history: {
            entries: [{ query: text, results: mergedResults, timestamp, resultCount: mergedResults.length }],
            index: 0,
          },
        },
      });
    };

    if (!hasActiveSpace) {
      try {
        const { default: useSystemStore } = await import('../../../stores/systemStore');
        const newSpace = await useSystemStore.getState().actions.createSpace({
          name: `Search: ${text}`,
          skipDefaultWindow: true,
          skipProjectLimit: true,
          settings: { isChatVisible: false, viewMode: 'focused', sidebar: { isVisible: true, width: 300, mode: 'normal' } },
        });
        if (newSpace?.id) {
          const entity = await buildSearchEntity(newSpace.id);
          const entityId = (entity as any)._id || (entity as any).id;
          navigate(`/spaces/${newSpace.id}?openEntity=${entityId}`);
        }
      } catch (err) {
        log.error('[GlobalEntitySearch] Failed to launch search in new space:', err);
      }
      return;
    }

    try {
      const entity = await buildSearchEntity(currentSpaceId);
      const entityId = (entity as any)._id || (entity as any).id;
      executeCommand('space_open_window', {
        entityId,
        callback: (res: string) => {
          try {
            const parsed = JSON.parse(res);
            if (!parsed.success) log.error('[GlobalEntitySearch] Failed to open search window:', parsed.error);
          } catch (err) {
            log.error('[GlobalEntitySearch] Failed to parse space_open_window result:', err);
          }
        },
      });
    } catch (err) {
      log.error('[GlobalEntitySearch] Failed to launch search:', err);
    }
  }, [inputValue, onClose, currentSpaceId, hasActiveSpace, backendResults, clientResults, navigate, executeCommand]);

  const isEmpty = !inputValue.trim();
  const hasResults = totalCount > 0;
  const showLaunchAsPrompt = !isEmpty && (inputValue.trim().length > 30 || totalCount === 0);
  const showSearch = !isEmpty;
  const selectedActionLabel = isShiftHeld && hasActiveSpace ? 'Open here' : 'Jump to';
  // Prompt item (if shown) is index 0; search item (if shown) follows; then entity results,
  // then Drive view results at the end.
  const promptOffset = showLaunchAsPrompt ? 1 : 0;
  const entityOffset = promptOffset + (showSearch ? 1 : 0);
  const driveViewOffset = entityOffset + totalCount;
  const navCount = totalCount + matchingDriveViews.length + entityOffset;

  // ── Keyboard navigation ───────────────────────────────────────────────────

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (activeZone === null) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'quickbar') {
        setActiveZone('results');
        setSelectedIndex(0);
      } else {
        setSelectedIndex(i => Math.min(i + 1, navCount - 1));
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (activeZone === 'results' && selectedIndex === 0) {
        setActiveZone('quickbar');
        setQuickbarIndex(0);
      } else if (activeZone === 'results') {
        setSelectedIndex(i => Math.max(i - 1, 0));
      } else if (activeZone === 'quickbar') {
        setActiveZone(null);
      }
    } else if (e.key === 'ArrowLeft' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight' && activeZone === 'quickbar') {
      e.preventDefault();
      setQuickbarIndex(i => Math.min(i + 1, QUICKBAR_ITEMS.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Cmd+Enter always launches in a new space when the prompt item is visible
      if (e.metaKey && showLaunchAsPrompt) {
        handleLaunchInNewSpace();
        return;
      }
      // Shift+Enter opens in this space; plain Enter jumps to the entity
      const openHere = e.shiftKey && hasActiveSpace;
      if (activeZone === 'quickbar') {
        const item = QUICKBAR_ITEMS[quickbarIndex];
        if (item) fireQuickbarItem(item);
      } else if (activeZone === 'results') {
        if (showLaunchAsPrompt && selectedIndex === 0) {
          handleLaunchInCurrentSpace();
        } else if (showSearch && selectedIndex === promptOffset) {
          handleLaunchSearch();
        } else {
          const entityIdx = selectedIndex - entityOffset;
          if (entityIdx < clientResults.length) {
            openHere ? handleOpenHere(clientResults[entityIdx]) : handleJumpTo(clientResults[entityIdx]);
          } else if (entityIdx < totalCount) {
            const be = backendExtras[entityIdx - clientResults.length];
            if (be) openHere ? handleOpenHereBackend(be) : handleJumpToBackend(be);
          } else {
            const dv = matchingDriveViews[entityIdx - totalCount];
            if (dv) handleSelectDriveView(dv);
          }
        }
      } else {
        // No zone active — fire first entity result, or drive view, or search/prompt
        if (clientResults.length > 0) {
          openHere ? handleOpenHere(clientResults[0]) : handleJumpTo(clientResults[0]);
        } else if (backendExtras.length > 0) {
          openHere ? handleOpenHereBackend(backendExtras[0]) : handleJumpToBackend(backendExtras[0]);
        } else if (matchingDriveViews.length > 0) {
          handleSelectDriveView(matchingDriveViews[0]);
        } else if (showSearch) {
          handleLaunchSearch();
        } else if (showLaunchAsPrompt) {
          handleLaunchInCurrentSpace();
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [
    activeZone, quickbarIndex, selectedIndex, entityOffset, promptOffset, navCount,
    clientResults, backendExtras, totalCount, matchingDriveViews, showLaunchAsPrompt, showSearch,
    hasActiveSpace,
    fireQuickbarItem, handleJumpTo, handleOpenHere, handleJumpToBackend, handleOpenHereBackend,
    handleSelectDriveView, handleLaunchInCurrentSpace, handleLaunchInNewSpace, handleLaunchSearch, onClose,
  ]);

  return (
    <div className="global-entity-search-overlay" onClick={onClose}>
      <div className="global-entity-search" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="global-entity-search-header">
          <MagnifyingGlass size={20} className="search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="global-entity-search-input"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything..."
            autoComplete="off"
            spellCheck={false}
          />
          {backendLoading && <span className="search-spinner" />}
        </div>

        {/* ── Quickbar ── */}
        <div
          className={`global-entity-search-quickbar${activeZone === 'quickbar' ? ' is-active' : ''}`}
          ref={quickbarRef}
        >
          <div className="quickbar-creates">
            {CREATE_ITEMS.map((item, i) => (
              <button
                key={item.id}
                className={`quickbar-item${activeZone === 'quickbar' && quickbarIndex === i ? ' focused' : ''}`}
                onMouseDown={e => { e.preventDefault(); fireQuickbarItem(item); }}
                onMouseEnter={() => { setActiveZone('quickbar'); setQuickbarIndex(i); }}
                tabIndex={-1}
                title={item.label}
              >
                <span className="quickbar-item-icon">{item.icon}</span>
                <span className="quickbar-item-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="quickbar-divider" />
          <div className="quickbar-apps">
            {APP_ITEMS.map((item, i) => {
              const globalIdx = CREATE_ITEMS.length + i;
              return (
                <button
                  key={item.id}
                  className={`quickbar-item${activeZone === 'quickbar' && quickbarIndex === globalIdx ? ' focused' : ''}`}
                  onMouseDown={e => { e.preventDefault(); fireQuickbarItem(item); }}
                  onMouseEnter={() => { setActiveZone('quickbar'); setQuickbarIndex(globalIdx); }}
                  tabIndex={-1}
                  title={item.label}
                >
                  <span className="quickbar-item-icon">{item.icon}</span>
                  <span className="quickbar-item-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Results ── */}
        <div className="global-entity-search-results" ref={listRef}>
          {!hasResults && isEmpty && !showLaunchAsPrompt ? (
            <div className="global-entity-search-empty">No entities available</div>
          ) : (
            <>
              {/* Prompt item always at top when visible */}
              {showLaunchAsPrompt && (
                <div
                  className={`global-entity-search-item launch-as-prompt ${activeZone === 'results' && selectedIndex === 0 ? 'selected' : ''}`}
                  onClick={handleLaunchInCurrentSpace}
                  onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(0); }}
                >
                  <div className="entity-icon">
                    <Sparkle size={16} weight="duotone" />
                  </div>
                  <div className="entity-content">
                    <div className="entity-name">Ask River</div>
                    <div className="entity-path">{inputValue.trim().length > 50 ? `${inputValue.trim().slice(0, 50)}…` : inputValue.trim()}</div>
                  </div>
                  <button
                    className="launch-in-new-space-chip"
                    onMouseDown={e => e.preventDefault()}
                    onClick={e => { e.stopPropagation(); handleLaunchInNewSpace(); }}
                    tabIndex={-1}
                    title="Open in new space"
                  >
                    <ArrowUpRight size={10} />
                    New space
                    <span className="chip-hotkey">⌘↵</span>
                  </button>
                </div>
              )}

              {/* Search item — always shown when there is a query */}
              {showSearch && (
                <div
                  className={`global-entity-search-item launch-as-search ${activeZone === 'results' && selectedIndex === promptOffset ? 'selected' : ''}`}
                  onClick={handleLaunchSearch}
                  onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(promptOffset); }}
                >
                  <div className="entity-icon">
                    <MagnifyingGlass size={16} weight="duotone" />
                  </div>
                  <div className="entity-content">
                    <div className="entity-name">Search</div>
                    <div className="entity-path">{inputValue.trim().length > 50 ? `${inputValue.trim().slice(0, 50)}…` : inputValue.trim()}</div>
                  </div>
                </div>
              )}

              {!hasResults && !isEmpty && matchingDriveViews.length === 0 && (
                <div className="global-entity-search-empty">Nothing found for "{inputValue}"</div>
              )}

              {isEmpty && (
                <div className="search-section-label">
                  <Clock size={11} weight="bold" /> Recent
                </div>
              )}

              {clientResults.map((entity, i) => {
                const itemIdx = i + entityOffset;
                const parentName = getParentName(entity._id);
                const spaceName = getSpaceName(entity._id);
                const showSpace = spaceName && spaceName !== parentName;
                const pathText = parentName
                  ? (showSpace ? `in ${parentName} · ${spaceName}` : `in ${parentName}`)
                  : (spaceName ? `in ${spaceName}` : null);
                const isSelected = activeZone === 'results' && itemIdx === selectedIndex;
                return (
                  <div
                    key={entity._id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => e.shiftKey && hasActiveSpace ? handleOpenHere(entity) : handleJumpTo(entity)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(itemIdx); }}
                  >
                    <div className="entity-icon">
                      {(entity.entityType === 'AppInstance' || entity.entityType === 'System.AppInstance')
                        ? <AppSquircle appId={(entity.skeleton as any)?.appId || ''} size="sm" />
                        : getEntityIcon(entity)}
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{entity.name || 'Unnamed'}</div>
                      {pathText && <div className="entity-path">{pathText}</div>}
                    </div>
                    <span className="entity-type-label">{isSelected ? selectedActionLabel : typeLabel(entity.entityType || '')}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {backendExtras.length > 0 && (
                <div className="search-section-label">Content matches</div>
              )}

              {backendExtras.map((result, i) => {
                const idx = clientResults.length + i + entityOffset;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                const bParentName = getParentName(result.entity_id);
                const bSpaceName = getSpaceName(result.entity_id);
                const bShowSpace = bSpaceName && bSpaceName !== bParentName;
                const bPathText = bParentName
                  ? (bShowSpace ? `in ${bParentName} · ${bSpaceName}` : `in ${bParentName}`)
                  : (bSpaceName ? `in ${bSpaceName}` : null);
                return (
                  <div
                    key={result.entity_id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={(e) => e.shiftKey && hasActiveSpace ? handleOpenHereBackend(result) : handleJumpToBackend(result)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">
                      {getEntityIcon({ entityType: result.entityType, name: result.name, _id: result.entity_id } as BaseEntityType)}
                    </div>
                    <div className="entity-content">
                      <div className="entity-name">{result.name}</div>
                      {bPathText
                        ? <div className="entity-path">{bPathText}</div>
                        : result.snippet && <div className="entity-path">{result.snippet}</div>
                      }
                    </div>
                    <span className="entity-type-label">{isSelected ? selectedActionLabel : typeLabel(result.entityType)}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}

              {matchingDriveViews.length > 0 && (
                <div className="search-section-label">Drive</div>
              )}

              {matchingDriveViews.map((item, i) => {
                const idx = driveViewOffset + i;
                const isSelected = activeZone === 'results' && idx === selectedIndex;
                return (
                  <div
                    key={item.id}
                    className={`global-entity-search-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectDriveView(item)}
                    onMouseEnter={() => { setActiveZone('results'); setSelectedIndex(idx); }}
                  >
                    <div className="entity-icon">{item.icon}</div>
                    <div className="entity-content">
                      <div className="entity-name">{item.label}</div>
                      <div className="entity-path">Drive</div>
                    </div>
                    <span className="entity-type-label">{isSelected ? 'Open' : 'View'}</span>
                    <ArrowUpRight size={12} className="entity-arrow" />
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="global-entity-search-footer">
          <div className="keyboard-hints">
            <div className="hint">
              <span className="key">↑↓</span>
              <span>Navigate</span>
            </div>
            <div className="hint">
              <span className="key">←→</span>
              <span>Quickbar</span>
            </div>
            <div className={`hint${isShiftHeld && hasActiveSpace ? '' : ' hint--primary'}`}>
              <span className="key">↵</span>
              <span>Jump to</span>
            </div>
            {hasActiveSpace && (
              <div className={`hint${isShiftHeld ? ' hint--primary' : ''}`}>
                <span className="key">⇧↵</span>
                <span>Open here</span>
              </div>
            )}
            <div className="hint">
              <span className="key">esc</span>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalEntitySearch;
